import { useEffect, useState } from 'react';
import { get, ref } from 'firebase/database';
import { db, getEnvDbPrefix } from '@/core/firebase/config';
import { getDateKeyInTimezone } from '@/shared/utils/dates';
import { useSeasonalFlags } from '@/shared/providers/SeasonalFlagsProvider';
import { DEFAULT_TIMEZONE, resolveTimeZone } from '@/features/wrap/utils/timezone';

async function loadPreferredTimeZone(userId: string): Promise<string> {
  const officeId = (await get(ref(db, getEnvDbPrefix(`user/${userId}/officeId`)))).val();
  if (typeof officeId !== 'string' || !officeId) return DEFAULT_TIMEZONE;
  const timeZone = (await get(ref(db, getEnvDbPrefix(`office/${officeId}/timezone`)))).val();
  return resolveTimeZone(timeZone);
}

export interface WrapSeason {
  year: number;
  /** True once Remote Config and the office timezone have loaded and `wrap_enabled` is on. */
  isOpen: boolean;
  isLoading: boolean;
}

export default function useWrapSeason(userId: string | null | undefined): WrapSeason {
  const { wrapEnabled, ready } = useSeasonalFlags();
  const [loaded, setLoaded] = useState<{ userId: string; timeZone: string } | null>(null);

  useEffect(() => {
    if (!userId) return;
    let active = true;
    loadPreferredTimeZone(userId)
      .catch(error => {
        console.error('Failed to load the office timezone:', error);
        return DEFAULT_TIMEZONE;
      })
      .then(timeZone => {
        if (active) setLoaded({ userId, timeZone });
      });
    return () => {
      active = false;
    };
  }, [userId]);

  const timeZone = loaded && loaded.userId === userId ? loaded.timeZone : null;
  const [year] = getDateKeyInTimezone(new Date(), timeZone ?? DEFAULT_TIMEZONE).split('-');

  return {
    year: Number(year),
    isOpen: ready && timeZone !== null && wrapEnabled,
    isLoading: !ready || (!!userId && timeZone === null),
  };
}
